import { useContext } from 'react';
import {
  ApolloError,
  LazyQueryExecFunction,
  MutationTuple,
} from '@apollo/client';
import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';

import { AlertContext } from 'app/shared/context/Alert';
import { NotifyContext } from 'app/shared/context/Notify';

type ApolloAction =
  | LazyQueryExecFunction<any, any>
  | MutationTuple<any, any>[0];

type ActionVariables<TAction extends ApolloAction> =
  NonNullable<Parameters<TAction>[0]> extends { variables?: infer V }
    ? V
    : Record<string, any>;

type ResponseWithData<TAction extends ApolloAction> = Omit<
  Awaited<ReturnType<TAction>>,
  'data' | 'error'
> & {
  data: NonNullable<Awaited<ReturnType<TAction>>['data']>;
  error?: never;
};

interface UseSubmitActionParameters<TAction extends ApolloAction> {
  beforeSubmit?: (values: any) => void;
  submitAction: TAction;
  submitVariables: (values: any) => ActionVariables<TAction>;
  successMsg?: string;
  successMsgValuesForDisplay?: {
    [key: string]: (values: any) => string;
  };
  failureMsg: string;
  onSuccess: (
    response: ResponseWithData<TAction>,
    resetForm?: FormikHelpers<any>['resetForm']
  ) => void;
  onError?: (error: any, values: any) => void;
  onValidationError?: (validationErrors: { [key: string]: string }) => void;
  validationErrorKeysForDisplay?: { [key: string]: string };
  messageContext?: 'notify' | 'alert';
}

export const useDisplayMessage = (messageContext: 'notify' | 'alert') => {
  const notifyContext = useContext(NotifyContext);
  const alertContext = useContext(AlertContext);

  return (messageType: any, messageText?: string) => {
    if (messageContext === 'alert') {
      alertContext.addAlert({
        type: messageType,
        message: messageText || '',
      });
    } else {
      notifyContext.addMessage(messageText || '');
    }
  };
};

const formatValidationErrorsForDisplay = (
  validationErrors: { [key: string]: string | string[] },
  keysForDisplay: { [key: string]: string } = {}
): { [key: string]: string } =>
  Object.keys(validationErrors).reduce(
    (obj, key) => {
      const validationError = Array.isArray(validationErrors[key])
        ? validationErrors[key][0]
        : validationErrors[key];

      return {
        ...obj,
        [key]: /^general.*/.test(key)
          ? validationError
          : `This ${keysForDisplay[key] || key} ${validationError}`,
      };
    },
    {} as { [key: string]: string }
  );

const setCustomGenericValidationError = (validationErrors: {
  [key: string]: string | string[];
}): { [key: string]: string | string[] } => {
  if (validationErrors && Object.keys(validationErrors)[0] === 'msg') {
    validationErrors.general = Object.values(validationErrors)[0];
    delete validationErrors.msg;
  }
  return validationErrors;
};

const setCustomNamedValidationError = (validationErrors: {
  [key: string]: string | string[];
}): { [key: string]: string | string[] } => {
  if (validationErrors && /^msg_.+$/.test(Object.keys(validationErrors)[0])) {
    const errKey = Object.keys(validationErrors)[0].replace(
      /^msg_/,
      'general_'
    );
    validationErrors[errKey] = Object.values(validationErrors)[0];
    delete validationErrors[Object.keys(validationErrors)[0]];
  }
  return validationErrors;
};

export const getValidationErrors = (
  error: ApolloError,
  keysForDisplay?: { [key: string]: string }
) => {
  const validationErrorIndicatorTextActiveRecordError = 'Record invalid';
  const validationErrorIndicatorTextCustomError = 'Validation Error';

  if (error && error.graphQLErrors) {
    // There are two kinds of validation errors returned by backend:
    // 1. ActiveRecord: Errors raised automatically by Rails ActiveRecord when a 'save' action fails
    //    a model validation, e.g. field uniqueness validations
    // 2. Custom: Errors raised manually by raising GraphQL::UserValidationError in a GraphQL endpoint,
    //    e.g. custom validations requiring more complex logic than an ActiveRecord model validation

    const validationError = error.graphQLErrors.find(
      (o: any) =>
        o.message === validationErrorIndicatorTextActiveRecordError ||
        o.message === validationErrorIndicatorTextCustomError
    );

    let validationErrors = validationError?.extensions?.validation_errors as {
      [key: string]: string | string[];
    };

    // If we have a GENERIC custom validation error ('msg'), store it in a pseudo-key named 'general'
    validationErrors = setCustomGenericValidationError(validationErrors);

    // If we have a NAMED custom validation error ('msg_blah_blah'), store it in a pseudo-key whose
    // name starts with 'general_' e.g. general_blah_blah
    validationErrors = setCustomNamedValidationError(validationErrors);

    if (validationErrors && Object.keys(validationErrors).length > 0) {
      return formatValidationErrorsForDisplay(validationErrors, keysForDisplay);
    } else {
      return null;
    }
  }

  return null;
};

const getValidationErrorsStr = (validationErrors: { [key: string]: string }) =>
  Object.keys(validationErrors)
    .map((key: string) => `${key} ${validationErrors[key]}`)
    .join(', ');

export const UseSubmitAction = <TAction extends ApolloAction = ApolloAction>({
  beforeSubmit,
  submitAction,
  submitVariables,
  successMsg,
  successMsgValuesForDisplay,
  failureMsg,
  onSuccess,
  onError,
  onValidationError,
  validationErrorKeysForDisplay,
  messageContext = 'notify',
}: UseSubmitActionParameters<TAction>) => {
  const intl = useIntl();
  const displayMessage = useDisplayMessage(messageContext);
  const failureMsgFull = intl.formatMessage(
    {
      id: 'submitAction.failureMsgFull',
    },
    {
      failureMsg,
    }
  );

  const getSuccessMsg = (values: any) => {
    if (successMsgValuesForDisplay) {
      for (const valueName of Object.keys(successMsgValuesForDisplay)) {
        const regex = new RegExp(`\\[${valueName}\\]`);
        successMsg = successMsg?.replace(
          regex,
          successMsgValuesForDisplay[valueName](values)
        );
      }
    }
    return successMsg;
  };

  const showTechnicalError = () =>
    process.env.NODE_ENV !== 'production' ||
    process.env.SHOW_MUTATION_TECH_ERRORS === 'true';

  const failureMsgFullTechnical = (error: string) => {
    return `${failureMsgFull} (${intl.formatMessage({
      id: 'submitAction.technicalError',
    })}: ${error})`;
  };

  const getFailureMsg = (error: string) =>
    showTechnicalError() ? failureMsgFullTechnical(error) : failureMsgFull;

  return async (
    values?: any,
    {
      resetForm,
      setSubmitting,
    }: {
      resetForm?: FormikHelpers<any>['resetForm'];
      setSubmitting?: FormikHelpers<any>['setSubmitting'];
    } = {}
  ) => {
    const resolvedValues = (values ?? {}) as any;
    try {
      setSubmitting && setSubmitting(true);

      if (beforeSubmit) {
        beforeSubmit(resolvedValues);
      }
      const response = (await submitAction({
        variables: submitVariables(resolvedValues),
      })) as Awaited<ReturnType<TAction>>;

      if ('error' in response && response.error) {
        throw response.error;
      } else if (response.data) {
        if (successMsg) {
          displayMessage('success', getSuccessMsg(resolvedValues));
        }
        onSuccess(response as ResponseWithData<TAction>, resetForm);
      }

      setSubmitting && setSubmitting(false);
    } catch (e: any) {
      const validationErrors = getValidationErrors(
        e,
        validationErrorKeysForDisplay
      );
      if (validationErrors) {
        if (onValidationError) {
          onValidationError(validationErrors);
        } else {
          displayMessage('error', getValidationErrorsStr(validationErrors));
        }
      } else {
        onError && onError(e, resolvedValues);
        displayMessage('error', getFailureMsg(e.message));
      }
      setSubmitting && setSubmitting(false);
    }
  };
};
